"use client";

import { EventStatus, UserStatus } from "@finbackoffice/enums";
import { ISiteConfigLayouts, MenuButtonsEnum } from "@finbackoffice/fe-core";
import {
    AuthContext,
    ChatContext,
    useRuntimeConfig,
    useSearchParamsQuery,
    useSiteConfig,
} from "@finbackoffice/site-core";
import { usePathnameWithLocal } from "hooks";
import {
    BetSlipContextV2,
    BetSlipItemsContext,
    ConfigContextV2,
    CrmContext,
    IModalForwardRefPropsV2,
    ModalsContextV2,
    MultiBetBonusProviderV2,
    UserAccountContextV2,
} from "contexts";
import classNames from "classnames";
import dynamic from "next/dynamic";
import { useParams, useRouter } from "next/navigation";
import {
    FC,
    RefObject,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import Button from "components/base/button/Button";
import ErrorBoundary from "components/base/error-boundary/ErrorBoundary";
import { Svg } from "components/base/svg/Svg";
import { AssetsDirectory } from "@finbackoffice/enums";
import { ActiveSportGameContext } from "contexts";
import { validationRules } from "utils/betValidation";
import { ModalTypes, RouteNames } from "utils/constants";
import { ModalStyleTypeEnum } from "components/base/modal/constants/modal-config";
import Modal from "components/base/modal/Modal";
import Translate from "../base/translate/Translate";
import styles from "./menu.module.sass";

const CasinoTools = dynamic(() => import("components/menu/casino-tools/CasinoTools"), {
    ssr: false,
});

const FloatingBetslip = dynamic(() => import("./floating-betslip/FloatingBetslip"), {
    ssr: false,
});

type MenuScopeType = "default" | "sport" | "casino";

const ProfileButton = () => {
    const { userProfileStatus, loadProfile } = useContext(UserAccountContextV2);
    const { unreadMessages, chatStats } = useContext(ChatContext);
    const { unreadMessagesCount } = useContext(CrmContext);
    const { currentModal, setCurrentModal, accountModalRef } = useContext(ModalsContextV2);

    const unreadMessagesMemo =
        unreadMessages.length + (chatStats?.unread_message ?? 0) + unreadMessagesCount;

    const onProfileClick = useCallback(async () => {
        if (currentModal === ModalTypes.ACCOUNT) {
            setCurrentModal(null);
        } else {
            await loadProfile();
            accountModalRef.current?.open();
        }
    }, [currentModal, setCurrentModal, loadProfile, accountModalRef]);

    return (
        <Button
            type="button"
            variant="custom"
            onClick={onProfileClick}
            className={classNames(
                styles.profile,
                currentModal === ModalTypes.ACCOUNT && styles.active,
            )}
            data-testid="open-user-panel-button">
            <Translate tid="menu_profile" />
            <span>
                <Svg
                    src={`/${AssetsDirectory.Mobile}/menu-login-icon.svg`}
                    className="svg-icon"
                    prefixAppName
                />
                {Boolean(unreadMessagesMemo) && (
                    <span className={styles.unreadMsgsCount}>{unreadMessagesMemo}</span>
                )}
                {userProfileStatus && userProfileStatus !== UserStatus.Registered && (
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/question-icon.svg`}
                        className={styles.verificationReminder}
                    />
                )}
            </span>
        </Button>
    );
};

const LoginButton = () => {
    const { currentModal, setCurrentModal, loginModalRef } = useContext(ModalsContextV2);

    const onLoginClick = useCallback(() => {
        if (currentModal === ModalTypes.LOGIN) {
            setCurrentModal(null);
        } else {
            loginModalRef.current?.open();
        }
    }, [currentModal, loginModalRef, setCurrentModal]);

    return (
        <>
            <Button
                type="button"
                variant="custom"
                onClick={onLoginClick}
                className={classNames(
                    styles.login,
                    currentModal === ModalTypes.LOGIN && styles.active,
                )}
                data-testid="header-login-button">
                <Translate tid="menu_login" />
                <Svg
                    src={`/${AssetsDirectory.Mobile}/menu-login-icon.svg`}
                    className="svg-icon"
                    prefixAppName
                />
            </Button>
        </>
    );
};

const Menu: FC = () => {
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const siteLayoutsConfig = useSiteConfig<ISiteConfigLayouts>("layouts");
    const router = useRouter();
    const params = useParams();
    const { pathname, pathnameValues, pathnameWithoutLocale: path } = usePathnameWithLocal();
    const { query } = useSearchParamsQuery();
    const { gameId, sport, tournamentId, liveGameRoutes } = query;
    const { isUserLoggedIn } = useContext(AuthContext);
    const { currentModal, setCurrentModal, betSlipModalRef } = useContext(ModalsContextV2);
    const { betProcessing, betSlipError } = useContext(BetSlipContextV2);
    const { betItems } = useContext(BetSlipItemsContext);
    const { currentGameStatus, setCurrentGameStatus } = useContext(ActiveSportGameContext);
    const { isSportMultiBetBonusesEnabled } = useContext(ConfigContextV2);
    const [betCountChange, setBetCountChange] = useState(false);
    const [liveButtonForceActive, setLiveButtonForceActive] = useState<boolean>(
        currentGameStatus === EventStatus.Live,
    );
    const casinoToolsModalRef: RefObject<IModalForwardRefPropsV2 | null> = useRef(null);
    const casinoScope = useMemo(() => pathnameValues[1], [pathnameValues]);

    const scope = useMemo(() => {
        const chunk = pathnameValues[0] as MenuScopeType;
        switch (chunk) {
            case "casino":
            case "sport":
                return chunk as MenuScopeType;
            default:
                return "default";
        }
    }, [pathnameValues]);

    useEffect(() => {
        if ([ModalTypes.CASINO_TOOLS].includes(currentModal || "")) {
            setCurrentModal(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [casinoScope]);

    useEffect(() => {
        if (liveButtonForceActive) {
            setLiveButtonForceActive(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    useEffect(() => {
        if (currentGameStatus === EventStatus.Live) {
            setLiveButtonForceActive(true);
            setCurrentGameStatus(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentGameStatus]);

    useEffect(() => {
        if (betItems.length) {
            setBetCountChange(true);

            setTimeout(() => {
                setBetCountChange(false);
            }, 400);
        }
    }, [betItems.length]);

    const onBetSlipButtonClickHandler = useCallback(() => {
        if (currentModal === ModalTypes.BET_SLIP) {
            setCurrentModal(null);
        } else {
            betSlipModalRef.current?.open();
        }
    }, [betSlipModalRef, currentModal, setCurrentModal]);

    const handleLiveButtonClick = useCallback(() => {
        const pathToLive = `/${RouteNames.SPORT}/${RouteNames.LIVE}/${RouteNames.ALL}`;
        const pathToCalendar = `/${RouteNames.SPORT}/${RouteNames.CALENDAR}`;
        const isLiveView = pathname?.includes(pathToLive);
        const isCalendarView = pathname?.includes(pathToCalendar);

        if (!(isLiveView || isCalendarView)) {
            router.push(pathToLive);
        } else if (isLiveView && !!liveGameRoutes) {
            const sportSlug = liveGameRoutes[0];
            router.push(`${pathToLive}/${sportSlug}`);
        }
    }, [pathname, liveGameRoutes, router]);

    const handleSportButtonClick = useCallback(() => {
        const pathToPrematch = `/${RouteNames.SPORT}/${RouteNames.PREMATCH}`;
        const isPrematchView = pathname?.includes(pathToPrematch);
        if (!isPrematchView) {
            router.push(`${pathToPrematch}/${RouteNames.TOP}`);
        } else if (!!tournamentId || !!gameId) {
            router.push(`${pathToPrematch}/${RouteNames.ALL}/${sport}`);
        }
    }, [pathname, sport, gameId, tournamentId, router]);

    const hasBetSlipError = useMemo(
        () =>
            validationRules.doesAnyBetHaveError(betItems, betSlipError) ||
            !!validationRules.getRemovableBets(betItems).length,
        [betSlipError, betItems],
    );

    const renderFloatingBetslip = useMemo(() => {
        if (
            COMMON_SITE_CONFIGS.betslip.floatingBetslip &&
            siteLayoutsConfig.menu[scope].includes(MenuButtonsEnum.BetSlip)
        ) {
            return <FloatingBetslip />;
        }
        return null;
    }, [COMMON_SITE_CONFIGS.betslip.floatingBetslip, scope, siteLayoutsConfig.menu]);

    const onCasinoToolsClick = useCallback(() => {
        if (currentModal === ModalTypes.CASINO_TOOLS) {
            setCurrentModal(null);
        } else {
            casinoToolsModalRef.current?.open();
        }
    }, [currentModal, setCurrentModal]);

    const onCasinoClick = useCallback(() => {
        const isCasinoLobbyView = path === `/${RouteNames.CASINO}`;
        if (!isCasinoLobbyView) {
            router.push(`/${RouteNames.CASINO}`);
        }
    }, [path, router]);

    const renderMainNavigation = useCallback(
        (name: string) => {
            const gameId = query?.gameId;
            switch (name) {
                case MenuButtonsEnum.Prematch: {
                    const pathToPrematch = `/${RouteNames.SPORT}/${RouteNames.PREMATCH}`;
                    const isPrematchView = pathname?.includes(pathToPrematch);
                    return (
                        <Button
                            key={name}
                            type="button"
                            variant="custom"
                            onClick={handleSportButtonClick}
                            className={classNames(
                                styles.sport,
                                !liveButtonForceActive && isPrematchView && styles.active,
                            )}>
                            <>
                                {isPrematchView && (!!params?.gameId || !!params?.tournamentId) && (
                                    <i className={styles.navigateTo} />
                                )}
                                <p>
                                    <Translate tid="pageName_sport" />
                                </p>
                                <Svg
                                    src={`/${AssetsDirectory.Mobile}/menu-sport.svg`}
                                    className="svg-icon"
                                    prefixAppName
                                />
                            </>
                        </Button>
                    );
                }
                case MenuButtonsEnum.LiveCasino: {
                    const liveCasinoViewHref = `/${RouteNames.CASINO}/${RouteNames.LIVE_CASINO}`;
                    const isLiveCasinoView = pathname?.includes(liveCasinoViewHref);
                    const handleLiveCasinoClick = () => {
                        if (!isLiveCasinoView || (isLiveCasinoView && gameId)) {
                            router.push(liveCasinoViewHref);
                        }
                    };
                    return (
                        <Button
                            type="button"
                            variant="custom"
                            onClick={handleLiveCasinoClick}
                            key={name}
                            href={liveCasinoViewHref}
                            className={classNames(
                                styles.liveCasino,
                                isLiveCasinoView && styles.active,
                            )}>
                            <>
                                {isLiveCasinoView && gameId && <i className={styles.navigateTo} />}
                                <p>
                                    <Translate tid="pageName_livecasino" />
                                </p>
                                <Svg
                                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.BaseIcons}/live-casino-icon.svg`}
                                    className="svg-icon"
                                    prefixAppName
                                />
                            </>
                        </Button>
                    );
                }
                case MenuButtonsEnum.BetSlip:
                    return (
                        <Button
                            key={name}
                            type="button"
                            variant="custom"
                            onClick={onBetSlipButtonClickHandler}
                            className={classNames(
                                styles.betslip,
                                currentModal === ModalTypes.BET_SLIP && styles.betSlipActive,
                            )}
                            data-testid="open-betslip-button">
                            <div
                                className={classNames(
                                    hasBetSlipError && styles.betslipHasErrorBtn,
                                )}>
                                <Svg
                                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/menu-betslip.svg`}
                                />
                                <span className={classNames(betCountChange && styles.animate)}>
                                    {betItems.length}
                                    {betProcessing && (
                                        <span className={styles.spinner}>
                                            <Svg
                                                src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/spinner.svg`}
                                            />
                                        </span>
                                    )}
                                </span>
                            </div>
                            <p>
                                <Translate tid="menu_betSlip" />
                            </p>
                        </Button>
                    );
                case MenuButtonsEnum.Slots: {
                    const slotsViewHref = `/${RouteNames.CASINO}/${RouteNames.SLOTS}`;
                    const isSlotsView = pathname?.includes(slotsViewHref);

                    const handleSlotsClick = () => {
                        if (!isSlotsView || (isSlotsView && gameId)) {
                            router.push(slotsViewHref);
                        }
                    };
                    return (
                        <Button
                            type="button"
                            variant="custom"
                            onClick={handleSlotsClick}
                            key={name}
                            className={classNames(styles.slots, isSlotsView && styles.active)}>
                            <>
                                {isSlotsView && gameId && <i className={styles.navigateTo} />}
                                <p>
                                    <Translate tid="pageName_slots" />
                                </p>
                                <Svg
                                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.BaseIcons}/slots-icon.svg`}
                                    className="svg-icon"
                                    prefixAppName
                                />
                            </>
                        </Button>
                    );
                }
                case MenuButtonsEnum.Live:
                    return (
                        <Button
                            key={name}
                            type="button"
                            variant="custom"
                            onClick={handleLiveButtonClick}
                            className={classNames(
                                styles.live,
                                (liveButtonForceActive ||
                                    pathname?.includes(RouteNames.CALENDAR) ||
                                    pathname?.includes(
                                        `/${RouteNames.SPORT}/${RouteNames.LIVE}`,
                                    )) &&
                                    styles.active,
                            )}>
                            <>
                                {(params?.liveGameRoutes as string[])?.length > 0 && (
                                    <i className={styles.navigateTo} />
                                )}
                                <p>
                                    <Translate tid="pageName_live" />
                                </p>
                                <Svg
                                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/menu-live.svg`}
                                    className="svg-icon"
                                />
                            </>
                        </Button>
                    );
                case MenuButtonsEnum.Casino:
                    return (
                        <Button
                            key={name}
                            type="button"
                            variant="custom"
                            onClick={onCasinoClick}
                            className={classNames(
                                styles.casino,
                                pathname?.includes(`/${RouteNames.CASINO}`) &&
                                    !pathname?.includes(RouteNames.POLICIES) &&
                                    styles.active,
                            )}>
                            {pathname?.includes(`/${RouteNames.CASINO}/`) && gameId && (
                                <i className={styles.navigateTo} />
                            )}
                            <p>
                                <Translate tid="menu_casino" />
                            </p>
                            <Svg
                                src={`/${AssetsDirectory.Mobile}/menu-casino.svg`}
                                className="svg-icon"
                                prefixAppName
                            />
                        </Button>
                    );
                case MenuButtonsEnum.CasinoTools:
                    return (
                        <Button
                            key={name}
                            type="button"
                            variant="custom"
                            onClick={onCasinoToolsClick}
                            className={classNames(
                                styles.casinoButton,
                                currentModal === ModalTypes.CASINO_TOOLS &&
                                    styles.casinoButtonActive,
                            )}>
                            <div>
                                <Svg
                                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/providers.svg`}
                                    className={styles.providers}
                                />
                                <Svg
                                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/menu-betslip.svg`}
                                />
                                <span className={styles.arrow} />
                                <Svg
                                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/search.svg`}
                                    className={styles.search}
                                />
                            </div>
                            <p>
                                <Translate tid="menu_casino_tools" />
                            </p>
                        </Button>
                    );
                default:
                    return null;
            }
        },
        [
            query?.gameId,
            onBetSlipButtonClickHandler,
            currentModal,
            hasBetSlipError,
            betCountChange,
            betItems.length,
            betProcessing,
            handleLiveButtonClick,
            liveButtonForceActive,
            pathname,
            params?.liveGameRoutes,
            params?.gameId,
            params?.tournamentId,
            onCasinoClick,
            onCasinoToolsClick,
            handleSportButtonClick,
            router,
        ],
    );

    const renderContent = useMemo(
        () => (
            <>
                {renderFloatingBetslip}
                <div className={styles.bottomMenuContainer}>
                    <div className={styles.menu} data-testid="bottom-menu">
                        {siteLayoutsConfig?.menu?.[scope]?.map(renderMainNavigation)}
                        <Modal
                            ref={casinoToolsModalRef}
                            type={ModalTypes.CASINO_TOOLS}
                            styleConfigType={ModalStyleTypeEnum.CasinoTools}
                            animateVariant="bottom"
                            styleClass={styles.menuCasinoModal}>
                            <CasinoTools />
                        </Modal>

                        {isUserLoggedIn ? <ProfileButton /> : <LoginButton />}
                    </div>
                </div>
            </>
        ),
        [
            isUserLoggedIn,
            scope,
            renderFloatingBetslip,
            renderMainNavigation,
            siteLayoutsConfig?.menu,
        ],
    );

    return (
        <ErrorBoundary name={Menu.name}>
            {isSportMultiBetBonusesEnabled ? (
                <MultiBetBonusProviderV2>{renderContent}</MultiBetBonusProviderV2>
            ) : (
                renderContent
            )}
        </ErrorBoundary>
    );
};
export default Menu;
